import { BadRequestException, Injectable } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';
import { ActivityService } from './activity.service';

export type AuditQuery = Record<string, string | undefined>;

const DEFAULT_TAKE = 25;
const MAX_TAKE = 200;

/**
 * Bookkeeping columns stripped from the rendered diff.
 *
 * They are still stored — the row keeps the complete before/after — but a
 * create otherwise renders a dozen lines of `id`, `createdOn`, `sortOrder`
 * before the one field a reader is actually looking for.
 */
const PLUMBING_FIELDS = new Set([
  'id', 'createdOn', 'createdAt', 'createdBy', 'updatedOn', 'updatedAt', 'updatedBy',
  'sortOrder', 'isActive', 'deletedAt', 'deletedBy', 'storeId', 'organizationId',
]);

/**
 * Read side of the audit log.
 *
 * Everything here filters and pages in SQL. The admin page previously fetched
 * one unfiltered page and filtered it in the browser, which made the server's
 * filter parameters dead code and made the UI's own filters misleading — they
 * narrowed the fetched page, not the log.
 */
@Injectable()
export class AuditQueryService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService
  ) {}

  /**
   * Fails closed.
   *
   * The equivalent helper on CommerceAdminService falls back to 'store_demo'
   * when store context is missing, so a caller with no resolved store silently
   * receives the demo store's rows. For an audit endpoint that is the wrong
   * direction to fail in: refuse rather than serve someone else's history.
   */
  private storeId(): string {
    const context = this.storeContext.current();

    if (!context.resolved || !context.storeId) {
      throw new BadRequestException(
        'Audit queries require a resolved store context (send x-store-id).'
      );
    }

    return context.storeId;
  }

  private paging(query: AuditQuery): { take: number; skip: number } {
    const take = Number(query.take ?? DEFAULT_TAKE);
    const skip = Number(query.skip ?? 0);

    return {
      take: Number.isFinite(take) ? Math.min(Math.max(take, 1), MAX_TAKE) : DEFAULT_TAKE,
      skip: Number.isFinite(skip) ? Math.max(skip, 0) : 0,
    };
  }

  private dateRange(query: AuditQuery): { gte?: Date; lte?: Date } | undefined {
    const from = query.from ? new Date(query.from) : undefined;
    const to = query.to ? new Date(query.to) : undefined;

    const valid = (d?: Date) => (d && !Number.isNaN(d.getTime()) ? d : undefined);
    const gte = valid(from);
    const lte = valid(to);

    return gte || lte ? { ...(gte ? { gte } : {}), ...(lte ? { lte } : {}) } : undefined;
  }

  // ── Batches — the grouped default view ──────────────────────────────────

  async listBatches(query: AuditQuery) {
    const { take, skip } = this.paging(query);
    const range = this.dateRange(query);

    const where: Prisma.ChangeBatchWhereInput = {
      storeId: this.storeId(),
      ...(query.action ? { action: query.action } : {}),
      ...(query.actorId ? { actorId: query.actorId } : {}),
      ...(query.correlationId ? { correlationId: query.correlationId } : {}),
      ...(range ? { startedAt: range } : {}),
      ...(query.search
        ? {
            OR: [
              { summary: { contains: query.search, mode: 'insensitive' } },
              { action: { contains: query.search, mode: 'insensitive' } },
              { actorName: { contains: query.search, mode: 'insensitive' } },
              { route: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    const [items, total] = await Promise.all([
      this.prisma.changeBatch.findMany({ where, orderBy: { startedAt: 'desc' }, take, skip }),
      this.prisma.changeBatch.count({ where }),
    ]);

    return { items, total };
  }

  /** Header plus its detail rows — the expand step of the grouped list. */
  async getBatch(batchId: string) {
    const storeId = this.storeId();

    // Authorise on the HEADER, then fetch detail by batchId alone. Many audited
    // tables have no storeId column of their own (order_items, product_variants,
    // inventory_levels), so their trigger rows can legitimately carry a NULL
    // storeId — filtering the detail by store would silently hide exactly the
    // rows the batch exists to show.
    const batch = await this.prisma.changeBatch.findFirst({ where: { batchId, storeId } });
    if (!batch) return { batch: null, changes: [] };

    const changes = await this.prisma.changeLog.findMany({
      where: { batchId },
      orderBy: { changedOn: 'asc' },
      take: MAX_TAKE,
    });

    return { batch, changes: changes.map((c) => this.withDiff(c)) };
  }

  // ── Flat change list ────────────────────────────────────────────────────

  async listChanges(query: AuditQuery) {
    const { take, skip } = this.paging(query);
    const range = this.dateRange(query);

    const where: Prisma.ChangeLogWhereInput = {
      storeId: this.storeId(),
      ...(query.schemaName ? { schemaName: query.schemaName } : {}),
      ...(query.tableName ? { tableName: query.tableName } : {}),
      ...(query.recordId ? { recordId: query.recordId } : {}),
      ...(query.operation ? { operation: query.operation.toLowerCase() } : {}),
      ...(query.changedBy ? { changedBy: query.changedBy } : {}),
      ...(query.batchId ? { batchId: query.batchId } : {}),
      ...(query.correlationId ? { correlationId: query.correlationId } : {}),
      ...(range ? { changedOn: range } : {}),
      ...(query.search
        ? {
            OR: [
              { tableName: { contains: query.search, mode: 'insensitive' } },
              { recordId: { contains: query.search, mode: 'insensitive' } },
              { changedBy: { contains: query.search, mode: 'insensitive' } },
              { actorName: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    const [items, total] = await Promise.all([
      this.prisma.changeLog.findMany({ where, orderBy: { changedOn: 'desc' }, take, skip }),
      this.prisma.changeLog.count({ where }),
    ]);

    return { items: items.map((i) => this.withDiff(i)), total };
  }

  /** "Everything that ever happened to order X." */
  async recordTimeline(query: AuditQuery) {
    if (!query.tableName || !query.recordId) {
      throw new BadRequestException('tableName and recordId are required');
    }

    const { take, skip } = this.paging(query);

    const where: Prisma.ChangeLogWhereInput = {
      storeId: this.storeId(),
      tableName: query.tableName,
      recordId: query.recordId,
      ...(query.schemaName ? { schemaName: query.schemaName } : {}),
    };

    const [items, total] = await Promise.all([
      this.prisma.changeLog.findMany({ where, orderBy: { changedOn: 'desc' }, take, skip }),
      this.prisma.changeLog.count({ where }),
    ]);

    return { items: items.map((i) => this.withDiff(i)), total };
  }

  // ── Interface log ───────────────────────────────────────────────────────

  async listInterfaceLogs(query: AuditQuery) {
    const { take, skip } = this.paging(query);
    const range = this.dateRange(query);

    // A status *range* ("4" meaning 4xx) rather than an exact code — the useful
    // question is almost always "did it fail", not "was it exactly 422".
    const statusFilter = this.statusFilter(query.statusRange, query.statusCode);

    const where: Prisma.InterfaceLogWhereInput = {
      storeId: this.storeId(),
      ...(query.method ? { method: query.method.toUpperCase() } : {}),
      ...(query.direction ? { direction: query.direction } : {}),
      ...(query.path ? { path: { contains: query.path, mode: 'insensitive' } } : {}),
      ...(query.correlationId ? { correlationId: query.correlationId } : {}),
      ...(query.userId ? { userId: query.userId } : {}),
      ...(query.batchId ? { batchId: query.batchId } : {}),
      ...(statusFilter ? { statusCode: statusFilter } : {}),
      ...(range ? { requestedOn: range } : {}),
      ...(query.minDurationMs ? { durationMs: { gte: Number(query.minDurationMs) || 0 } } : {}),
      ...(query.search
        ? {
            OR: [
              { path: { contains: query.search, mode: 'insensitive' } },
              { correlationId: { contains: query.search, mode: 'insensitive' } },
              { userId: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    const [items, total] = await Promise.all([
      this.prisma.interfaceLog.findMany({ where, orderBy: { requestedOn: 'desc' }, take, skip }),
      this.prisma.interfaceLog.count({ where }),
    ]);

    return { items, total };
  }

  async getInterfaceLog(id: string) {
    return this.prisma.interfaceLog.findFirst({ where: { id, storeId: this.storeId() } });
  }

  private statusFilter(
    statusRange: string | undefined,
    statusCode: string | undefined
  ): Prisma.IntNullableFilter | undefined {
    if (statusCode) {
      const exact = Number(statusCode);
      return Number.isFinite(exact) ? { equals: exact } : undefined;
    }

    if (!statusRange) return undefined;

    const bucket = Number(statusRange);
    if (!Number.isFinite(bucket) || bucket < 1 || bucket > 5) return undefined;

    return { gte: bucket * 100, lt: (bucket + 1) * 100 };
  }

  // ── Activity ────────────────────────────────────────────────────────────

  async listActivity(query: AuditQuery) {
    const { take, skip } = this.paging(query);
    const range = this.dateRange(query);

    const where: Prisma.ActivityLogWhereInput = {
      storeId: this.storeId(),
      ...(query.eventName ? { eventName: query.eventName } : {}),
      ...(query.sessionId ? { sessionId: query.sessionId } : {}),
      ...(query.userId ? { userId: query.userId } : {}),
      ...(query.anonymousId ? { anonymousId: query.anonymousId } : {}),
      ...(query.app ? { app: query.app } : {}),
      ...(range ? { receivedAt: range } : {}),
      ...(query.search
        ? {
            OR: [
              { route: { contains: query.search, mode: 'insensitive' } },
              { eventName: { contains: query.search, mode: 'insensitive' } },
              { targetId: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    const [items, total] = await Promise.all([
      this.prisma.activityLog.findMany({ where, orderBy: { receivedAt: 'desc' }, take, skip }),
      this.prisma.activityLog.count({ where }),
    ]);

    return { items, total };
  }

  // ── Correlation ─────────────────────────────────────────────────────────

  /**
   * One correlation id, everything it touched: the inbound request, the
   * outbound calls it made, and every database row it changed.
   *
   * This was impossible before — no call site ever passed a correlation id to
   * the change log, so `change_log.correlationId` was NULL on every row.
   */
  async correlation(correlationId: string) {
    const storeId = this.storeId();

    const [calls, changes, batches] = await Promise.all([
      this.prisma.interfaceLog.findMany({
        where: { correlationId, storeId },
        orderBy: { requestedOn: 'asc' },
        take: MAX_TAKE,
      }),
      this.prisma.changeLog.findMany({
        where: { correlationId, storeId },
        orderBy: { changedOn: 'asc' },
        take: MAX_TAKE,
      }),
      this.prisma.changeBatch.findMany({ where: { correlationId, storeId }, take: 10 }),
    ]);

    return {
      correlationId,
      inbound: calls.filter((c) => c.direction === 'inbound'),
      outbound: calls.filter((c) => c.direction === 'outbound'),
      changes: changes.map((c) => this.withDiff(c)),
      batches,
    };
  }

  // ── Facets and stats ────────────────────────────────────────────────────

  /** Real values from the data, so filter dropdowns offer what exists rather than a hardcoded guess. */
  async facets() {
    const storeId = this.storeId();

    const [tables, actions, actors, events] = await Promise.all([
      this.prisma.changeLog.groupBy({
        by: ['schemaName', 'tableName'],
        where: { storeId },
        _count: { _all: true },
        orderBy: { _count: { tableName: 'desc' } },
        take: 40,
      }),
      this.prisma.changeBatch.groupBy({
        by: ['action'],
        where: { storeId, action: { not: null } },
        _count: { _all: true },
        orderBy: { _count: { action: 'desc' } },
        take: 40,
      }),
      this.prisma.changeBatch.groupBy({
        by: ['actorId', 'actorName'],
        where: { storeId, actorId: { not: null } },
        _count: { _all: true },
        orderBy: { _count: { actorId: 'desc' } },
        take: 25,
      }),
      this.prisma.activityLog.groupBy({
        by: ['eventName'],
        where: { storeId },
        _count: { _all: true },
        orderBy: { _count: { eventName: 'desc' } },
        take: 40,
      }),
    ]);

    return {
      tables: tables.map((t) => ({
        schemaName: t.schemaName,
        tableName: t.tableName,
        count: t._count._all,
      })),
      actions: actions.map((a) => ({ action: a.action, count: a._count._all })),
      actors: actors.map((a) => ({ actorId: a.actorId, actorName: a.actorName, count: a._count._all })),
      events: events.map((e) => ({ eventName: e.eventName, count: e._count._all })),
      allowedEvents: ActivityService.allowedEvents(),
    };
  }

  async stats() {
    const storeId = this.storeId();
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000);

    const [batches, changes, calls, failures, activity] = await Promise.all([
      this.prisma.changeBatch.count({ where: { storeId, startedAt: { gte: since } } }),
      this.prisma.changeLog.count({ where: { storeId, changedOn: { gte: since } } }),
      this.prisma.interfaceLog.count({ where: { storeId, requestedOn: { gte: since } } }),
      this.prisma.interfaceLog.count({
        where: { storeId, requestedOn: { gte: since }, statusCode: { gte: 400 } },
      }),
      this.prisma.activityLog.count({ where: { storeId, receivedAt: { gte: since } } }),
    ]);

    return { batches, changes, calls, failures, activity, windowHours: 24 };
  }

  // ── Export ──────────────────────────────────────────────────────────────

  /** Filtered range as rows for CSV, for a compliance request. */
  async exportChanges(query: AuditQuery) {
    const { items } = await this.listChanges({ ...query, take: String(MAX_TAKE) });

    return items.map((item) => ({
      changedOn: item.changedOn,
      entity: `${item.schemaName}.${item.tableName}`,
      recordId: item.recordId,
      operation: item.operation,
      changedColumns: Array.isArray(item.changedColumns) ? item.changedColumns.join(' ') : '',
      actor: item.actorName ?? item.changedBy,
      actorRole: item.actorRole ?? '',
      ipAddress: item.ipAddress ?? '',
      correlationId: item.correlationId ?? '',
      batchId: item.batchId ?? '',
    }));
  }

  /**
   * Turns the stored before/after pair into a per-field diff.
   *
   * Done server-side so every consumer renders the same thing, and so the
   * response carries only the fields that changed rather than two complete
   * copies of the row for the client to compare.
   *
   * `txId` is rendered as a string on the way out. It is a Postgres bigint, so
   * Prisma hands back a JS BigInt — and JSON.stringify THROWS on BigInt rather
   * than dropping it, which turned every response containing a trigger-written
   * row into a 500. That was invisible for a while because the rows written by
   * the old application-side path have a NULL txId and serialise fine.
   */
  private withDiff<T extends { changedColumns: unknown; oldValue: unknown; newValue: unknown; txId?: bigint | null }>(
    row: T
  ): Omit<T, 'txId'> & { txId: string | null; diff: { field: string; from: unknown; to: unknown }[] } {
    const columns = Array.isArray(row.changedColumns) ? (row.changedColumns as string[]) : [];
    const oldValue = (row.oldValue ?? {}) as Record<string, unknown>;
    const newValue = (row.newValue ?? {}) as Record<string, unknown>;

    const fields = columns.length ? columns : Object.keys(newValue ?? {});

    const diff = fields
      .filter((field) => !PLUMBING_FIELDS.has(field))
      .map((field) => ({ field, from: oldValue?.[field], to: newValue?.[field] }))
      .filter((d) => d.from !== undefined || d.to !== undefined);

    return { ...row, txId: row.txId?.toString() ?? null, diff };
  }
}
